import { formatCurrency } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { ChartService } from '@services/chart.service';
import { AgChartsModule } from 'ag-charts-angular';
import {
    AgCartesianSeriesTooltipRendererParams,
    AgChartLabelFormatterParams,
    AgChartOptions
} from 'ag-charts-community';
import { FormatNumberWithSuffixPipe } from 'ngx-lib';

export interface ISalesFunnelChartConfig {
    title?: string;
    labels?: {
        current?: string;
        previous?: string;
    };
    data: ISalesFunnelChartData[];
    descriptionHelpText?: string;
}
export interface ISalesFunnelChartData {
    category: string;
    current: number;
    previous: number;
}

@Component({
    selector: 'app-sales-funnel-chart',
    templateUrl: './sales-funnel-chart.component.html',
    styleUrls: ['./sales-funnel-chart.component.scss'],
    imports: [AgChartsModule],
    providers: [FormatNumberWithSuffixPipe]
})
export class SalesFunnelChartComponent {
    private readonly chartService = inject(ChartService);
    private readonly formatNumberWithSuffixPipe = inject(FormatNumberWithSuffixPipe);

    readonly config = input<ISalesFunnelChartConfig>();

    commonConfig = {
        label: {
            formatter: ({ value }: AgChartLabelFormatterParams<number>) => {
                return value > 0 ? this.formatNumberWithSuffixPipe.transform(value) : '';
            }
        },
        tooltip: {
            renderer: this.renderer
        }
    };

    readonly chartOptions = computed<AgChartOptions | undefined>(() => {
        const config = this.config();
        if (!config) return undefined;

        return {
            theme: this.chartService.getTheme(),
            height: 250,
            legend: {
                toggleSeries: false
            },
            title: {
                text: config.title
            },
            data: config.data,
            series: [
                {
                    type: 'bar',
                    xKey: 'category',
                    yKey: 'previous',
                    yName: config.labels?.previous,
                    ...this.commonConfig
                },
                {
                    type: 'bar',
                    xKey: 'category',
                    yKey: 'current',
                    yName: config.labels?.current,
                    ...this.commonConfig
                }
            ],
            axes: [
                {
                    position: 'left',
                    title: {
                        text: 'Amount'
                    },
                    type: 'number',
                    label: {
                        formatter: ({ value }) => {
                            return this.formatNumberWithSuffixPipe.transform(value);
                        }
                    }
                },
                {
                    position: 'bottom',
                    type: 'category'
                }
            ]
        };
    });

    private renderer({ datum, xKey, yKey }: AgCartesianSeriesTooltipRendererParams) {
        return {
            title: datum[xKey],
            content: formatCurrency(Number(datum[yKey]), 'en-US', '$')
        };
    }
}
